import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPromptGroups,
  createPromptGroup,
  updatePromptGroup,
  deletePromptGroup,
  reorderPromptGroups,
  assignPromptToGroup,
  bulkAssignPromptsToGroup,
} from '../../services/monitor/prompt/PromptGroupService';

const promptGroupsKey = (projectId: number) => ['prompt-groups', projectId];

export const usePromptGroups = (projectId: number) => {
  return useQuery({
    queryKey: promptGroupsKey(projectId),
    queryFn: () => getPromptGroups(projectId),
    enabled: !!projectId,
  });
};

export const useCreatePromptGroup = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ name, color }: { name: string; color?: string }) =>
      createPromptGroup(projectId, name, color),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptGroupsKey(projectId) });
    },
  });
};

export const useUpdatePromptGroup = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      groupId,
      data,
    }: {
      groupId: number;
      data: { name?: string; color?: string };
    }) => updatePromptGroup(projectId, groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptGroupsKey(projectId) });
    },
  });
};

export const useDeletePromptGroup = (projectId: number, onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: number) => deletePromptGroup(projectId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptGroupsKey(projectId) });
      onSuccessCallback?.();
    },
  });
};

export const useReorderPromptGroups = (projectId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: number[]) => reorderPromptGroups(projectId, orderedIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptGroupsKey(projectId) });
    },
  });
};

export const useAssignPromptToGroup = (projectId: number, onSuccessCallback?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ promptId, groupId }: { promptId: number; groupId: number | null }) =>
      assignPromptToGroup(projectId, promptId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptGroupsKey(projectId) });
      onSuccessCallback?.();
    },
  });
};

export const useBulkAssignPromptsToGroup = (
  projectId: number,
  onSuccessCallback?: () => void
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ promptIds, groupId }: { promptIds: number[]; groupId: number | null }) =>
      bulkAssignPromptsToGroup(projectId, promptIds, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: promptGroupsKey(projectId) });
      onSuccessCallback?.();
    },
  });
};
